import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Save, Edit2, X, List } from 'lucide-react'
import { 
  fetchAutocompleteLists, 
  createAutocompleteItem, 
  updateAutocompleteItem, 
  deleteAutocompleteItem,
  type AutocompleteListItem 
} from '../utils/api'

interface EditingItem {
  id: string | null
  field_name: string
  name: string
  address: string
  nif: string
  sort_order: number
}

const EMPTY_ITEM: EditingItem = {
  id: null,
  field_name: 'seguradora',
  name: '',
  address: '',
  nif: '',
  sort_order: 0,
}

export function AdminAutocompletePage() {
  const [items, setItems] = useState<AutocompleteListItem[]>([])
  const [fieldNames, setFieldNames] = useState<string[]>([])
  const [selectedField, setSelectedField] = useState<string>('seguradora')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<EditingItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [newFieldName, setNewFieldName] = useState('')
  const [showNewFieldInput, setShowNewFieldInput] = useState(false)

  const loadData = async () => {
    try {
      const data = await fetchAutocompleteLists()
      setItems(data.items)
      const fields = Object.keys(data.lists)
      setFieldNames(fields.length > 0 ? fields : ['seguradora'])
      if (!fields.includes(selectedField) && fields.length > 0) {
        setSelectedField(fields[0])
      }
    } catch {
      setError('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const filteredItems = items.filter(item => item.field_name === selectedField)

  const handleEdit = (item: AutocompleteListItem) => {
    const data = JSON.parse(item.data || '{}')
    setEditing({
      id: item.id,
      field_name: item.field_name,
      name: item.name,
      address: data.address || '',
      nif: data.nif || '',
      sort_order: item.sort_order,
    })
  }

  const handleNew = () => {
    setEditing({
      ...EMPTY_ITEM,
      field_name: selectedField,
      sort_order: filteredItems.length + 1,
    })
  }

  const handleSave = async () => {
    if (!editing || !editing.name.trim()) return

    setSaving(true)
    setError(null)

    try {
      const data: Record<string, string> = {}
      if (editing.address) data.address = editing.address
      if (editing.nif) data.nif = editing.nif

      if (editing.id) {
        await updateAutocompleteItem(editing.id, {
          name: editing.name,
          data,
          sort_order: editing.sort_order,
        })
      } else {
        await createAutocompleteItem(editing.field_name, editing.name, data, editing.sort_order)
      }

      await loadData()
      setEditing(null)
    } catch {
      setError('Erro ao guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem a certeza que deseja eliminar este item?')) return

    try {
      await deleteAutocompleteItem(id)
      await loadData()
    } catch {
      setError('Erro ao eliminar')
    }
  }

  const handleAddNewField = () => {
    if (!newFieldName.trim()) return
    const fieldKey = newFieldName.toLowerCase().replace(/\s+/g, '_')
    if (!fieldNames.includes(fieldKey)) {
      setFieldNames([...fieldNames, fieldKey])
    }
    setSelectedField(fieldKey)
    setNewFieldName('')
    setShowNewFieldInput(false)
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Listas de Autocomplete</h1>
              <p className="text-sm text-gray-500">Gerir opções de preenchimento automático</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Field selector */}
        <div className="card p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <label className="font-medium text-gray-700">Campo:</label>
            <div className="flex gap-2 flex-wrap">
              {fieldNames.map((field) => (
                <button
                  key={field}
                  onClick={() => setSelectedField(field)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedField === field
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {field}
                </button>
              ))}
              {showNewFieldInput ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    placeholder="nome_campo"
                    className="input text-sm w-32"
                    autoFocus
                  />
                  <button onClick={handleAddNewField} className="btn-primary text-sm py-2">
                    Adicionar
                  </button>
                  <button onClick={() => setShowNewFieldInput(false)} className="p-2 text-gray-500 hover:text-gray-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewFieldInput(true)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Novo Campo
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Items list */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <List className="w-5 h-5 text-primary-600" />
              Opções para "{selectedField}"
            </h2>
            <button onClick={handleNew} className="btn-primary gap-2">
              <Plus className="w-4 h-4" />
              Adicionar
            </button>
          </div>

          {filteredItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <List className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma opção definida para este campo.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredItems.map((item) => {
                const data = JSON.parse(item.data || '{}')
                return (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      {data.address && (
                        <p className="text-sm text-gray-500">{data.address}</p>
                      )}
                      {data.nif && (
                        <p className="text-xs text-gray-400">NIF: {data.nif}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Edit modal */}
        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing.id ? 'Editar' : 'Adicionar'} Opção
              </h3>

              <div>
                <label className="label">Nome *</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="input"
                  placeholder="Ex: Fidelidade"
                />
              </div>

              <div>
                <label className="label">Morada (opcional)</label>
                <input
                  type="text"
                  value={editing.address}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                  className="input"
                  placeholder="Ex: Largo do Calhariz, 30, 1249-001 Lisboa"
                />
              </div>

              <div>
                <label className="label">NIF/NIPC (opcional)</label>
                <input
                  type="text"
                  value={editing.nif}
                  onChange={(e) => setEditing({ ...editing, nif: e.target.value })}
                  className="input"
                  placeholder="Ex: 500 918 880"
                />
              </div>

              <div>
                <label className="label">Ordem</label>
                <input
                  type="number"
                  value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })}
                  className="input w-24"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setEditing(null)}
                  className="btn-secondary"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary gap-2"
                  disabled={saving || !editing.name.trim()}
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p className="font-medium mb-1">💡 Como usar:</p>
          <p>
            As opções aqui definidas aparecem como sugestões de autocomplete nos formulários. 
            Para o campo <code className="bg-blue-100 px-1 rounded">seguradora</code>, os campos 
            <code className="bg-blue-100 px-1 rounded">morada_seguradora</code> e 
            <code className="bg-blue-100 px-1 rounded">nif_seguradora</code> são preenchidos automaticamente.
          </p>
        </div>
      </div>
    </div>
  )
}
